import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMateDetail, updateMate } from '../../../api';

const EditMate = () => {
    const navigate = useNavigate();
    const { mateId } = useParams();
    const [mateData, setMateData] = useState({
        name: '',
        avatar_history: '',
        agent_prompt: '',
        agent_prompt_en: '',
        agent_prompt_ua: '',
        agent_prompt_jp: '',
        agent_prompt_es: '',
        agent_prompt_de: '',
        agent_prompt_fr: '',
        voice: '',
        voice_instruction: ''
    });

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchMateInfo = async () => {
            try {
                setFetching(true);
                setError('');
                const data = await getMateDetail(mateId);

                // Only take the fields we want to edit or keep all
                setMateData({
                    ...data,
                    name: data.name || '',
                    avatar_history: data.avatar_history || '',
                    agent_prompt: data.agent_prompt || '',
                    agent_prompt_en: data.agent_prompt_en || '',
                    agent_prompt_ua: data.agent_prompt_ua || '',
                    agent_prompt_jp: data.agent_prompt_jp || '',
                    agent_prompt_es: data.agent_prompt_es || '',
                    agent_prompt_de: data.agent_prompt_de || '',
                    agent_prompt_fr: data.agent_prompt_fr || '',
                    voice: data.voice || '',
                    voice_instruction: data.voice_instruction || ''
                });
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load mate details.');
                console.error('Error fetching mate:', err);
            } finally {
                setFetching(false);
            }
        };

        if (mateId) {
            fetchMateInfo();
        }
    }, [mateId, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await updateMate(mateId, mateData);
            navigate(`/mate-details/${mateId}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update mate.');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 16px',
        borderRadius: '14px',
        border: '1px solid #cfd8dc',
        background: '#e5eff8',
        fontSize: '16px',
        color: '#0c1415',
        marginBottom: '16px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '8px',
        color: '#3c5a5d',
        fontWeight: '600',
        fontSize: '14px'
    };

    const sectionStyle = {
        background: '#F6F6F6',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #cfd8dc'
    };

    if (fetching) {
        return (
            <div style={{
                minHeight: '100vh',
                background: '#ccdff5',
                padding: '40px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ color: '#707F81', fontSize: '1.2rem' }}>Loading mate details...</div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#ccdff5', padding: '40px 20px' }}>
            <div style={{
                maxWidth: '1000px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <h1 style={{ color: '#3c5a5d', margin: 0, fontSize: '2.5rem' }}>
                        Edit Mate
                    </h1>
                    <button
                        onClick={() => navigate(`/mate-details/${mateId}`)}
                        className="btn btn-secondary"
                        style={{ padding: '10px 20px' }}
                    >
                        Cancel
                    </button>
                </div>

                {error && (
                    <div style={{ color: '#dc2626', marginBottom: '20px', padding: '16px', background: '#fee2e2', borderRadius: '12px', fontSize: '15px' }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Basic Info Section */}
                    <div style={sectionStyle}>
                        <h2 style={{ color: '#3c5a5d', marginBottom: '20px', fontSize: '1.5rem' }}>Basic Information</h2>

                        <label style={labelStyle}>Name *</label>
                        <input
                            type="text"
                            value={mateData.name}
                            onChange={(e) => setMateData({ ...mateData, name: e.target.value })}
                            required
                            style={inputStyle}
                            placeholder="Enter mate name"
                        />

                        <label style={labelStyle}>Avatar History</label>
                        <textarea
                            value={mateData.avatar_history}
                            onChange={(e) => setMateData({ ...mateData, avatar_history: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                            placeholder="Enter the backstory or history of this avatar..."
                        />
                    </div>

                    {/* Prompts Section */}
                    <div style={sectionStyle}>
                        <h2 style={{ color: '#3c5a5d', marginBottom: '20px', fontSize: '1.5rem' }}>Agent Prompts</h2>

                        <label style={labelStyle}>Default Prompt</label>
                        <textarea
                            value={mateData.agent_prompt}
                            onChange={(e) => setMateData({ ...mateData, agent_prompt: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                            placeholder="Default system prompt"
                        />

                        <label style={labelStyle}>English Prompt (en)</label>
                        <textarea
                            value={mateData.agent_prompt_en}
                            onChange={(e) => setMateData({ ...mateData, agent_prompt_en: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                        />

                        <label style={labelStyle}>Ukrainian Prompt (ua)</label>
                        <textarea
                            value={mateData.agent_prompt_ua}
                            onChange={(e) => setMateData({ ...mateData, agent_prompt_ua: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                        />

                        <label style={labelStyle}>Japanese Prompt (jp)</label>
                        <textarea
                            value={mateData.agent_prompt_jp}
                            onChange={(e) => setMateData({ ...mateData, agent_prompt_jp: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                        />

                        <label style={labelStyle}>Spanish Prompt (es)</label>
                        <textarea
                            value={mateData.agent_prompt_es}
                            onChange={(e) => setMateData({ ...mateData, agent_prompt_es: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                        />

                        <label style={labelStyle}>German Prompt (de)</label>
                        <textarea
                            value={mateData.agent_prompt_de}
                            onChange={(e) => setMateData({ ...mateData, agent_prompt_de: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                        />

                        <label style={labelStyle}>French Prompt (fr)</label>
                        <textarea
                            value={mateData.agent_prompt_fr}
                            onChange={(e) => setMateData({ ...mateData, agent_prompt_fr: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                        />
                    </div>

                    {/* Voice Settings Section */}
                    <div style={sectionStyle}>
                        <h2 style={{ color: '#3c5a5d', marginBottom: '20px', fontSize: '1.5rem' }}>Voice Settings</h2>

                        <label style={labelStyle}>Voice Name</label>
                        <input
                            type="text"
                            value={mateData.voice}
                            onChange={(e) => setMateData({ ...mateData, voice: e.target.value })}
                            style={inputStyle}
                            placeholder="e.g., Erinome, Alloy, Onyx..."
                        />

                        <label style={labelStyle}>Voice Instruction</label>
                        <textarea
                            value={mateData.voice_instruction}
                            onChange={(e) => setMateData({ ...mateData, voice_instruction: e.target.value })}
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                            placeholder="Describe how the voice should sound..."
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '16px', marginTop: '30px' }}>
                        <button
                            type="button"
                            onClick={() => navigate(`/mate-details/${mateId}`)}
                            className="btn btn-secondary"
                            style={{ flex: 1, padding: '16px', fontSize: '18px' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary"
                            style={{ flex: 1, padding: '16px', fontSize: '18px', opacity: loading ? 0.7 : 1 }}
                        >
                            {loading ? 'Saving Changes...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditMate;
